  import React, { useEffect, useState, useContext } from "react";
  import { useParams, useNavigate } from "react-router-dom";
  import { toast } from "react-hot-toast";
  import { useTranslation } from "../../../hooks/useTranslation";
  import LoaderComponent from "../../Loader/Loader";
  import SectionHeadingStyle3 from "../../SectionHeading/SectionHeadingStyle3";
  import Spacing from "../../Spacing";
  import QuickActions from "../../QuickActions/QuickActions";
  import styled from "styled-components";
  import { UserContext, fetchCryptoPricesBinance } from "../../../context/UserContext";

  const ClientDetails = ({ darkMode }) => {
    const { clientId } = useParams();
    const { translate } = useTranslation();
    const navigate = useNavigate();
    const { cryptoPrices, updateCryptoPrices } = useContext(UserContext);

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [activeTab, setActiveTab] = useState("overview");
    const [sharesBalanceBRL, setSharesBalanceBRL] = useState(0);
    const [sharesBalanceBTC, setSharesBalanceBTC] = useState(0);
    const [sharesBalanceUSDT, setSharesBalanceUSDT] = useState(0);
    const [sharesPendingBalance, setSharesPendingBalance] = useState(0);
    const [isDividendModalOpen, setIsDividendModalOpen] = useState(false);
    const [dividendAmount, setDividendAmount] = useState('');
    const [dividendCurrency, setDividendCurrency] = useState('BRL');

    // Add Transaction Modal State
    const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] = useState(false);
    const [newTransaction, setNewTransaction] = useState({
      type: 'deposit',
      amount: '',
      currency: 'BRL',
      description: '',
      status: 'completed',
      timestamp: new Date().toISOString().slice(0, 16) // Current date and time in local format
    });

    // Transactions state
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsCurrentPage, setTransactionsCurrentPage] = useState(1);
    const [transactionsTotalPages, setTransactionsTotalPages] = useState(1);
    const [transactionsCurrencyFilter, setTransactionsCurrencyFilter] = useState('ALL');
    const [transactionsTypeFilter, setTransactionsTypeFilter] = useState('ALL');

    // Shares state
    const [shares, setShares] = useState([]);
    const [sharesLoading, setSharesLoading] = useState(false);
    const [sharesCurrencyFilter, setSharesCurrencyFilter] = useState('ALL');

    const fetchCryptoPrices = async () => {
      try {
        const data = await fetchCryptoPricesBinance();
        updateCryptoPrices({
          btc: data.BTCBRL,
          usdt: data.USDTBRL,
        });
      } catch (error) {
        console.error("Erro ao buscar cotações:", error);
      }
    };

    const fetchShares = async () => {
      try {
        setSharesLoading(true);
        const token = localStorage.getItem("userToken");

        if (!token) return;

        // Buscar ações do cliente específico usando a nova API adminGetUserShares
        const response = await fetch(
          `https://admingetusershares-qj6yfdqbiq-uc.a.run.app?userId=${clientId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error("Erro ao buscar ações do cliente");
        }
        
        const data = await response.json();
        
        // Store shares data for detailed view
        const sharesData = data.shares || [];
        
        // Fetch additional project details for each share
        if (sharesData.length > 0) {
          try {
            const enrichedShares = await Promise.all(
              sharesData.map(async (share) => {
                if (share.projectId) {
                  try {
                    // Fetch project details from the public API
                    const projectResponse = await fetch(
                      `https://fetchfullprojectdescription-qj6yfdqbiq-uc.a.run.app?projectId=${share.projectId}`
                    );
                    
                    if (projectResponse.ok) {
                      const projectData = await projectResponse.json();
                      return {
                        ...share,
                        projectName: projectData.name || share.projectName,
                        projectDescription: projectData.description || share.projectDescription,
                        projectImage: projectData.image || share.projectImage,
                        projectCategory: projectData.category || share.projectCategory,
                        projectLocation: projectData.location || share.projectLocation,
                        projectReturn: projectData.expectedReturn || share.projectReturn,
                      };
                    }
                  } catch (projectError) {
                    console.warn(`Could not fetch project details for ${share.projectId}:`, projectError);
                  }
                }
                return share;
              })
            );
            
            setShares(enrichedShares);
          } catch (enrichmentError) {
            console.warn('Error enriching shares with project details:', enrichmentError);
            setShares(sharesData);
          }
        } else {
          setShares([]);
        }
        
        // Calcular totais das ações do cliente
        if (sharesData.length > 0) {
          const { brlShares, btcShares, usdtShares } = sharesData.reduce((acc, share) => {
            if (share.currency === 'BTC') {
              // BTC: sharePrice is in satoshis, numberOfShares is the count
              const shareValue = share.numberOfShares * share.sharePrice;
              acc.btcShares += shareValue;
            } else if (share.currency === 'USDT') {
              // USDT: sharePrice is in cents, numberOfShares is the count
              const shareValue = share.numberOfShares * (share.sharePrice / 100);
              acc.usdtShares += shareValue;
            } else {
              // BRL: sharePrice is in cents, numberOfShares is the count
              const shareValue = share.numberOfShares * (share.sharePrice / 100);
              acc.brlShares += shareValue;
            }
            return acc;
          }, { brlShares: 0, btcShares: 0, usdtShares: 0 });

          setSharesBalanceBTC(btcShares);
          setSharesBalanceUSDT(usdtShares);
          setSharesBalanceBRL(brlShares);
        } else {
          // Se não há ações, zerar os valores
          setSharesBalanceBTC(0);
          setSharesBalanceUSDT(0);
          setSharesBalanceBRL(0);
        }
        
        setSharesPendingBalance(0); // Set to 0 for now, can be updated later
      } catch (error) {
        console.error("Erro ao buscar ações:", error);
        toast.error("Erro ao buscar ações do cliente");
      } finally {
        setSharesLoading(false);
      }
    };

    const fetchUserTransactions = async () => {
      try {
        const token = localStorage.getItem("userToken");

        if (!token) return;

        // A API já verifica se o usuário atual é admin
        // Não precisamos verificar accessLevel aqui

        setTransactionsLoading(true);

        console.log(`Fetching transactions for client: ${clientId}, page: ${transactionsCurrentPage}`);
        console.log('Current user access level:', user?.accessLevel);

        const response = await fetch(
          `https://admingetusertransactions-qj6yfdqbiq-uc.a.run.app?userId=${clientId}&page=${transactionsCurrentPage}&limit=10`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`API Error: ${response.status} - ${errorText}`);
          throw new Error(`Erro ao buscar transações do usuário: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        console.log('Transactions data received:', data);
        setTransactions(data.transactions || []);
        setTransactionsTotalPages(data.pagination?.totalPages || 1);
      } catch (error) {
        console.error("Erro ao buscar transações:", error);
        toast.error(`Erro ao buscar transações do usuário: ${error.message}`);
      } finally {
        setTransactionsLoading(false);
      }
    };

    useEffect(() => {
      const fetchUserDetails = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem("userToken");

          if (!token) {
            toast.error(translate("clientDetails.errorLoading"));
            return;
          }

          // Buscar cotações das criptomoedas
          await fetchCryptoPrices();

          const response = await fetch(
            `https://admincheckuserandgetinfo-qj6yfdqbiq-uc.a.run.app?userId=${clientId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          if (!response.ok) {
            throw new Error("Erro ao buscar detalhes do usuário");
          }
          
          const data = await response.json();
          setUser(data);
          
          // Buscar ações do cliente usando a nova API
          await fetchShares();
          
          // Como a nova API não retorna investimentos, 
          // vamos inicializar como array vazio por enquanto
          setInvestments([]);
          
          // Buscar transações do usuário após todos os dados básicos estarem carregados
          // Não verificar accessLevel aqui - a API já faz essa verificação
          await fetchUserTransactions();
        } catch (error) {
          console.error("Erro ao buscar detalhes do usuário:", error);
          toast.error(translate("clientDetails.errorLoading"));
        } finally {
          setLoading(false);
        }
      };

      if (clientId) {
        fetchUserDetails();
      }
    }, [clientId]);

    // Atualizar cotações a cada 30 segundos
    useEffect(() => {
      fetchCryptoPrices();
      const interval = setInterval(fetchCryptoPrices, 30000);
      return () => clearInterval(interval);
    }, []);

    // Refetch transactions only when page changes (not initial load)
    useEffect(() => {
      if (clientId && transactionsCurrentPage > 1) {
        fetchUserTransactions();
      }
    }, [transactionsCurrentPage]);



    const formatDate = (timestamp) => {
      if (!timestamp) return "N/A";
      if (timestamp._seconds) {
        return new Date(timestamp._seconds * 1000).toLocaleDateString("pt-BR");
      }
      return new Date(timestamp).toLocaleDateString("pt-BR");
    };

    const formatBalance = (balance) => {
      return `R$ ${(balance / 100).toFixed(2)}`;
    };

    const getUserStatus = (user) => {
      if (user.accessLevel < 0) return "suspended";
      if (user.verified && user.accessLevel >= 0) return "active";
      if (!user.verified) return "inactive";
      return "pending";
    };

    const getStatusColor = (status) => {
      switch (status) {
        case "active":
          return "#10b981";
        case "inactive":
          return "#ef4444";
        case "pending":
          return "#f59e0b";
        case "suspended":
          return "#6b7280";
        default:
          return "#6b7280";
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case "active":
          return "Ativo";
        case "inactive":
          return "Inativo";
        case "pending":
          return "Pendente";
        case "suspended":
          return "Suspenso";
        default:
          return status;
      }
    };

    const getAccessLevelText = (level) => {
      if (level >= 2) return "Administrador";
      if (level === 1) return "Moderador";
      if (level === 0) return "Usuário";
      return "Suspenso";
    };

    const getAccessLevelColor = (level) => {
      if (level >= 2) return "#8b5cf6";
      if (level === 1) return "#06b6d4";
      if (level === 0) return "#10b981";
      return "#ef4444";
    };

    const calculateTotalInBRL = () => {
      try {
        if (!cryptoPrices?.btc || !cryptoPrices?.usdt || !user) {
          return "0.00";
        }

        const brlBalance = (user.balance || 0) / 100; // Convert from cents
        const btcBalance = (user.btcBalance || 0) / 100000000; // Convert from satoshis
        const usdtBalance = (user.usdtBalance || 0) / 100; // Convert from cents
        
        const btcInBRL = btcBalance * Number(cryptoPrices.btc);
        const usdtInBRL = usdtBalance * Number(cryptoPrices.usdt);
        
        const total = brlBalance + btcInBRL + usdtInBRL;
        return total.toFixed(2);
      } catch (error) {
        console.error("Erro ao calcular total:", error);
        return "0.00";
      }
    };

    const calculateProfit = () => {
      try {
        const totalInvested = (user.totalInvested || 0) / 100;
        const totalWithdrawn = (user.totalWithdrawn || 0) / 100;
        return totalWithdrawn - totalInvested;
      } catch (error) {
        return 0;
      }
    };

    const calculateTotalPatrimony = () => {
      try {
        if (!cryptoPrices?.btc || !cryptoPrices?.usdt || !user) {
          return "0.00";
        }

        const brlBalance = (user.balance || 0) / 100;
        const btcBalance = (user.btcBalance || 0) / 100000000;
        const usdtBalance = (user.usdtBalance || 0) / 100;
        
        const btcInBRL = btcBalance * Number(cryptoPrices.btc);
        const usdtInBRL = usdtBalance * Number(cryptoPrices.usdt);
        
        const total = brlBalance + btcInBRL + usdtInBRL;
        return total.toFixed(2);
      } catch (error) {
        console.error("Erro ao calcular patrimônio total:", error);
        return "0.00";
      }
    };

    const calculateDistributionPercentage = (assetType) => {
      try {
        if (!cryptoPrices?.btc || !cryptoPrices?.usdt || !user) {
          return 0;
        }

        const total = parseFloat(calculateTotalPatrimony());
        if (total === 0) return 0;

        let percentage = 0;
        if (assetType === 'brl') {
          const brlValue = (user.balance || 0) / 100;
          percentage = (brlValue / total) * 100;
        } else if (assetType === 'btc') {
          const btcValue = ((user.btcBalance || 0) / 100000000) * Number(cryptoPrices.btc);
          percentage = (btcValue / total) * 100;
        } else if (assetType === 'usdt') {
          const usdtValue = ((user.usdtBalance || 0) / 100) * Number(cryptoPrices.usdt);
          percentage = (usdtValue / total) * 100;
        }
        return Math.max(0, Math.min(100, percentage)).toFixed(1);
      } catch (error) {
        console.error("Erro ao calcular distribuição:", error);
        return 0;
      }
    };

    const calculateProfitability = () => {
      try {
        const totalInvested = (user.totalInvested || 0) / 100;
        if (totalInvested === 0) return 0;
        
        const profit = calculateProfit();
        const profitability = (profit / totalInvested) * 100;
        return profitability.toFixed(2);
      } catch (error) {
        return 0;
      }
    };

    const calculateTotalSharesInBRL = () => {
      try {
        if (!cryptoPrices?.btc || !cryptoPrices?.usdt) {
          return "0.00";
        }

        const sharesInBRL = sharesBalanceBRL;
        const sharesInBTC = (sharesBalanceBTC / 100000000) * Number(cryptoPrices.btc);
        const sharesInUSDT = sharesBalanceUSDT * Number(cryptoPrices.usdt);
        const sharesTotal = sharesInBRL + sharesInBTC + sharesInUSDT;
        
        return sharesTotal.toFixed(2);
      } catch (error) {
        console.error("Erro ao calcular total de ações:", error);
        return "0.00";
      }
    };

    const handleAddDividend = (currency = 'BRL') => {
      setDividendAmount('');
      setDividendCurrency(currency);
      setIsDividendModalOpen(true);
    };

    const handleQuickPercentage = (percentage) => {
      setDividendAmount(percentage.toString());
    };

    const handleSubmitDividend = async () => {
      try {
        if (!dividendAmount || parseFloat(dividendAmount) <= 0 || parseFloat(dividendAmount) > 100) {
          toast.error("Por favor, insira uma porcentagem válida entre 0 e 100%");
          return;
        }

        const token = localStorage.getItem("userToken");
        if (!token) {
          toast.error("Token não encontrado");
          return;
        }

        // Adicionar dividendo para todos os projetos do usuário
        const response = await fetch(
          `https://adddividend-qj6yfdqbiq-uc.a.run.app`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              userId: clientId,
              percentage: parseFloat(dividendAmount),
              currency: dividendCurrency,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Erro ao adicionar dividendo: ${errorData}`);
        }

        const result = await response.json();
        
        toast.success(`Dividendo de ${dividendAmount}% em ${dividendCurrency} adicionado com sucesso para ${result.processedProjects} projeto(s)!`);
        
        // Fechar modal e limpar campos
        setIsDividendModalOpen(false);
        setDividendAmount('');
        
              // Recarregar dados do usuário
      const userResponse = await fetch(
        `https://admincheckuserandgetinfo-qj6yfdqbiq-uc.a.run.app?userId=${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
      
      // Recarregar ações do cliente usando a nova API
      await fetchShares();
        
      } catch (error) {
        console.error("Erro ao adicionar dividendo:", error);
        toast.error(`Erro ao adicionar dividendo: ${error.message}`);
      }
    };

    const handleCloseDividendModal = () => {
      setIsDividendModalOpen(false);
      setDividendAmount('');
      setDividendCurrency('BRL');
    };

    const handleAddTransaction = async () => {
      // Validate required fields
      if (!newTransaction.amount || parseFloat(newTransaction.amount) <= 0) {
        toast.error("Por favor, insira um valor válido");
        return;
      }

      try {
        const token = localStorage.getItem("userToken");
        if (!token) {
          toast.error("Token não encontrado");
          return;
        }

        // Convert amount to proper format based on currency
        let formattedAmount;
        switch (newTransaction.currency) {
          case 'BTC':
            // Convert BTC to satoshis (1 BTC = 100,000,000 satoshis)
            formattedAmount = Math.round(parseFloat(newTransaction.amount) * 100000000);
            break;
          case 'USDT':
          case 'BRL':
          default:
            // Convert to cents (1 unit = 100 cents)
            formattedAmount = Math.round(parseFloat(newTransaction.amount) * 100);
            break;
        }

        // Parse the selected timestamp
        const selectedDate = new Date(newTransaction.timestamp);
        const timestampSeconds = Math.floor(selectedDate.getTime() / 1000);

        // Prepare transaction data for API
        const transactionData = {
          userId: clientId,
          type: newTransaction.type,
          amount: formattedAmount,
          currency: newTransaction.currency,
          timestamp: timestampSeconds,
          status: newTransaction.status,
          description: newTransaction.description || ''
        };

        // Call the admin API to add transaction
        const response = await fetch(
          `https://adminaddusertransaction-qj6yfdqbiq-uc.a.run.app`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(transactionData),
          }
        );

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Erro ao adicionar transação: ${errorData}`);
        }

        const result = await response.json();
        console.log('API Response:', result);
        
        // Add the new transaction to the local state
        const newTransactionFromAPI = {
          id: result.transaction.id,
          amount: result.transaction.amount,
          type: result.transaction.type,
          currency: result.transaction.currency,
          timestamp: { _seconds: timestampSeconds },
          status: result.transaction.status,
          description: result.transaction.description,
          userId: clientId,
          isAdminAdded: true
        };
        
        console.log('New transaction object:', newTransactionFromAPI);

        setTransactions(prev => [newTransactionFromAPI, ...prev]);
        
        // Reset form and close modal
        setNewTransaction({
          type: 'deposit',
          amount: '',
          currency: 'BRL',
          description: '',
          status: 'completed',
          timestamp: new Date().toISOString().slice(0, 16)
        });
        setIsAddTransactionModalOpen(false);
        
        toast.success("Transação adicionada com sucesso!");
        
      } catch (error) {
        console.error("Erro ao adicionar transação:", error);
        toast.error(`Erro ao adicionar transação: ${error.message}`);
      }
    };

    // Transaction helper functions
    const formatTransactionDate = (timestamp) => {
      if (!timestamp) return "N/A";
      if (timestamp._seconds) {
        return new Date(timestamp._seconds * 1000).toLocaleDateString("pt-BR");
      }
      return new Date(timestamp).toLocaleDateString("pt-BR");
    };

    const formatTransactionTime = (timestamp) => {
      if (!timestamp) return "N/A";
      if (timestamp._seconds) {
        return new Date(timestamp._seconds * 1000).toLocaleTimeString("pt-BR");
      }
      return new Date(timestamp).toLocaleTimeString("pt-BR");
    };

    const formatTransactionAmount = (amount, type, currency = "BRL") => {
      const effectiveCurrency = currency || "BRL";
      const formattedAmount = (() => {
        switch (effectiveCurrency.toUpperCase()) {
          case "BRL":
          case "USDT":
            return (Math.abs(amount) / 100).toFixed(2);
          case "BTC":
            return (Math.abs(amount) / 100000000).toFixed(8);
          default:
            return Math.abs(amount);
        }
      })();

      const currencySymbol = {
        BRL: "R$",
        USDT: "USDT",
        BTC: "₿",
      }[effectiveCurrency.toUpperCase()] || "";

      const sign = type.toLowerCase() === "deposit" ||
                   type.toLowerCase() === "earning" ||
                   type.toLowerCase() === "commission" ? "+" : "-";

      return `${sign} ${currencySymbol} ${formattedAmount}`;
    };

    const handleTransactionsPageChange = (newPage) => {
      setTransactionsCurrentPage(newPage);
    };

    const handleViewProjectDetails = (projectId) => {
      // Navigate to project details page or open modal
      console.log(`Viewing project details for: ${projectId}`);
      // You can implement navigation or modal opening here
      toast.info(`Visualizando detalhes do projeto ${projectId}`);
    };

    // Filtered transactions
    const filteredTransactions = transactions.filter(transaction => 
      (transactionsCurrencyFilter === 'ALL' ? true : (transaction.currency || 'BRL') === transactionsCurrencyFilter) &&
      (transactionsTypeFilter === 'ALL' ? true : transaction.type.toLowerCase() === transactionsTypeFilter.toLowerCase())
    );

    // Filtered shares
    const filteredShares = shares.filter(share => 
      sharesCurrencyFilter === 'ALL' ? true : (share.currency || 'BRL') === sharesCurrencyFilter
    );

    if (loading) {
      return (
        <>
          <Spacing lg="145" md="80" />
          <div className="container">
            <LoaderContainer>
              <LoaderComponent size={50} borderThickness={6} />
            </LoaderContainer>
          </div>
        </>
      );
    }

    if (!user) {
      return (
        <>
          <Spacing lg="145" md="80" />
          <div className="container">
            <ErrorContainer>
              <h2>Usuário não encontrado</h2>
              <BackButton onClick={() => navigate("/clients-list")}>
                Voltar à Lista
              </BackButton>
            </ErrorContainer>
          </div>
        </>
      );
    }

    return (
      <>
        <Spacing lg="145" md="80" />
        <div className="container">
          <BackButton onClick={() => navigate("/clients-list")}>
            ← Voltar à Lista de Usuários
          </BackButton>
          
          <SectionHeadingStyle3
            title="Detalhes do Usuário"
            subTitle={user.name || "Nome não informado"}
            variant="cs_style_1 text-center"
          />
          <Spacing lg="90" md="45" />

          {/* Ações Rápidas */}
          <QuickActions variant="clientDetails" />

          <Spacing lg="60" md="30" />

          {/* Informações Gerais */}
          <UserInfoCard>
            <UserHeader>
              <UserAvatar src={user.avatar || "/perfil.png"} alt={user.name} />
              <UserInfo>
                <UserName>{user.name || "Nome não informado"}</UserName>
                <UserEmail>{user.email || "Email não informado"}</UserEmail>
                <UserId>ID: {user.uid}</UserId>
              </UserInfo>
              <StatusBadge color={getStatusColor(getUserStatus(user))}>
                {getStatusText(getUserStatus(user))}
              </StatusBadge>
            </UserHeader>

            <UserDetailsGrid>
              <DetailItem>
                <DetailLabel>CPF</DetailLabel>
                <DetailValue>{user.cpf || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Telefone</DetailLabel>
                <DetailValue>{user.phone || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Data de Nascimento</DetailLabel>
                <DetailValue>{user.birthdate || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Gênero</DetailLabel>
                <DetailValue>{user.gender || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Nacionalidade</DetailLabel>
                <DetailValue>{user.nationality || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Endereço</DetailLabel>
                <DetailValue>{user.address || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Renda</DetailLabel>
                <DetailValue>{user.income || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Consultor</DetailLabel>
                <DetailValue>{user.consultant ? "Sim" : "Não"}</DetailValue>
              </DetailItem>
            </UserDetailsGrid>
          </UserInfoCard>

          <Spacing lg="60" md="30" />

          {/* Informações Financeiras */}
          <FinancialInfoCard>
            <CardTitle>Informações Financeiras</CardTitle>
            
            <FinancialCardsContainer>
              <FinancialCard>
                <FinancialCardHeader>Saldo Atual</FinancialCardHeader>
                <FinancialCardBody>
                  <FinancialItem>
                    <FinancialLabel>Saldo BRL</FinancialLabel>
                    <FinancialValue>R$ {((user.balance || 0) / 100).toFixed(2)}</FinancialValue>
                  </FinancialItem>
                  <FinancialDivider />
                  <FinancialItem>
                    <FinancialLabel>Saldo BTC</FinancialLabel>
                    <FinancialValue>₿ {((user.btcBalance || 0) / 100000000).toFixed(8)}</FinancialValue>
                  </FinancialItem>
                  <FinancialDivider />
                  <FinancialItem>
                    <FinancialLabel>Saldo USDT</FinancialLabel>
                    <FinancialValue>$ {((user.usdtBalance || 0) / 100).toFixed(2)}</FinancialValue>
                  </FinancialItem>
                  <FinancialDivider />
                  <FinancialItem>
                    <FinancialLabel>Saldo Pendente</FinancialLabel>
                    <FinancialValue>R$ {((user.pendingBalance || 0) / 100).toFixed(2)}</FinancialValue>
                  </FinancialItem>
                  <FinancialDivider />
                  <FinancialItem>
                    <FinancialLabel>Total em BRL</FinancialLabel>
                    <FinancialValue highlight>R$ {calculateTotalInBRL()}</FinancialValue>
                  </FinancialItem>
                </FinancialCardBody>
              </FinancialCard>

              <FinancialCard>
                <FinancialCardHeader>Saldo em Ações</FinancialCardHeader>
                <FinancialCardBody>
                  <FinancialItem>
                    <FinancialLabel>Ações BRL</FinancialLabel>
                    <FinancialValue>R$ {(sharesBalanceBRL || 0).toFixed(2)}</FinancialValue>
                    <DividendButton onClick={() => handleAddDividend('BRL')}>
                      + Dividendo
                    </DividendButton>
                  </FinancialItem>
                  <FinancialItem>
                    <FinancialLabel>Debug BRL (raw):</FinancialLabel>
                    <FinancialValue style={{fontSize: '12px', color: '#888'}}>{sharesBalanceBRL || 0}</FinancialValue>
                  </FinancialItem>
                  <FinancialDivider />
                  <FinancialItem>
                    <FinancialLabel>Ações BTC</FinancialLabel>
                    <FinancialValue>₿ {(sharesBalanceBTC / 100000000 || 0).toFixed(8)}</FinancialValue>
                    <DividendButton onClick={() => handleAddDividend('BTC')}>
                      + Dividendo
                    </DividendButton>
                  </FinancialItem>

                  <FinancialItem>
                    <FinancialLabel>Ações USDT</FinancialLabel>
                    <FinancialValue>$ {(sharesBalanceUSDT || 0).toFixed(2)}</FinancialValue>
                    <DividendButton onClick={() => handleAddDividend('USDT')}>
                      + Dividendo
                    </DividendButton>
                  </FinancialItem>

                  <FinancialItem>
                    <FinancialLabel>Ações Pendentes</FinancialLabel>
                    <FinancialValue>R$ {(sharesPendingBalance || 0).toFixed(2)}</FinancialValue>
                  </FinancialItem>
                  <FinancialDivider />
                  <FinancialItem>
                    <FinancialLabel>Total BRL em Ações</FinancialLabel>
                    <FinancialValue highlight>R$ {calculateTotalSharesInBRL()}</FinancialValue>
                  </FinancialItem>

                </FinancialCardBody>
              </FinancialCard>
            </FinancialCardsContainer>

          </FinancialInfoCard>

          <Spacing lg="60" md="30" />

          {/* Detailed Shares per Project Section */}
          <DetailedSharesCard>
            <CardTitle>Detalhamento de Ações por Projeto</CardTitle>
            
            {!user ? (
              <SharesLoadingIndicator>
                <LoaderComponent size={30} borderThickness={4} />
                <span>Carregando dados do usuário...</span>
              </SharesLoadingIndicator>
            ) : (
              <>
                <SharesFilters>
                  <FilterSelect 
                    value={sharesCurrencyFilter}
                    onChange={(e) => setSharesCurrencyFilter(e.target.value)}
                  >
                    <option value="ALL">Todas as Moedas</option>
                    <option value="BRL">BRL</option>
                    <option value="BTC">BTC</option>
                    <option value="USDT">USDT</option>
                  </FilterSelect>
                </SharesFilters>

                {sharesLoading ? (
                  <SharesLoadingContainer>
                    <LoaderComponent size={50} borderThickness={6} />
                    <span>Carregando projetos...</span>
                  </SharesLoadingContainer>
                ) : filteredShares.length === 0 ? (
                  <SharesEmptyState>
                    <h3>
                      {sharesCurrencyFilter === 'ALL'
                        ? "Nenhuma ação encontrada"
                        : "Nenhuma ação encontrada com os filtros aplicados"}
                    </h3>
                    <p>Este usuário ainda não possui ações em nenhum projeto.</p>
                  </SharesEmptyState>
                ) : (
                  <>
                    <SharesGrid>
                      {filteredShares.map((share, index) => (
                        <ShareCard key={share.projectId || index}>
                          <ShareCardHeader>
                            <ProjectImage 
                              src={share.projectImage || "/edificio.png"} 
                              alt={share.projectName || "Projeto"}
                            />
                            <ProjectHeaderInfo>
                              <ProjectName>{share.projectName || `Projeto ${share.projectId || 'N/A'}`}</ProjectName>
                              <ProjectId>ID: {share.projectId || 'N/A'}</ProjectId>
                              <ProjectCategory>{share.projectCategory || 'Investimento'}</ProjectCategory>
                            </ProjectHeaderInfo>
                            <CurrencyBadge currency={share.currency}>
                              {share.currency || 'BRL'}
                            </CurrencyBadge>
                          </ShareCardHeader>

                          <ShareCardBody>
                            <ShareInfoRow>
                              <ShareInfoLabel>Quantidade de Ações:</ShareInfoLabel>
                              <ShareInfoValue>{share.numberOfShares || 0}</ShareInfoValue>
                            </ShareInfoRow>
                            
                            <ShareInfoRow>
                              <ShareInfoLabel>Preço por Ação:</ShareInfoLabel>
                              <ShareInfoValue>
                                {share.currency === 'BTC' 
                                  ? `₿ ${((share.sharePrice || 0) / 100000000).toFixed(8)}`
                                  : share.currency === 'USDT'
                                  ? `$ ${((share.sharePrice || 0) / 100).toFixed(2)}`
                                  : `R$ ${((share.sharePrice || 0) / 100).toFixed(2)}`
                                }
                              </ShareInfoValue>
                            </ShareInfoRow>

                            <ShareInfoRow>
                              <ShareInfoLabel>Valor Total:</ShareInfoLabel>
                              <ShareInfoValue highlight>
                                {share.currency === 'BTC' 
                                  ? `₿ ${(((share.sharePrice || 0) * (share.numberOfShares || 0)) / 100000000).toFixed(8)}`
                                  : share.currency === 'USDT'
                                  ? `$ ${(((share.sharePrice || 0) * (share.numberOfShares || 0)) / 100).toFixed(2)}`
                                  : `R$ ${(((share.sharePrice || 0) * (share.numberOfShares || 0)) / 100).toFixed(2)}`
                                }
                              </ShareInfoValue>
                            </ShareInfoRow>

                            <ShareInfoRow>
                              <ShareInfoLabel>Data de Compra:</ShareInfoLabel>
                              <ShareInfoValue>{formatDate(share.purchaseDate || share.timestamp)}</ShareInfoValue>
                            </ShareInfoRow>

                            <ShareInfoRow>
                              <ShareInfoLabel>Status:</ShareInfoLabel>
                              <ShareStatus status={share.status || 'active'}>
                                {share.status === 'active' ? 'Ativo' :
                                 share.status === 'pending' ? 'Pendente' :
                                 share.status === 'sold' ? 'Vendido' :
                                 share.status === 'cancelled' ? 'Cancelado' : 'Ativo'}
                              </ShareStatus>
                            </ShareInfoRow>

                            {share.projectDescription && (
                              <ProjectDescription>
                                <ShareInfoLabel>Descrição do Projeto:</ShareInfoLabel>
                                <ProjectDescriptionText>
                                  {share.projectDescription.length > 100 
                                    ? `${share.projectDescription.substring(0, 100)}...` 
                                    : share.projectDescription
                                  }
                                </ProjectDescriptionText>
                              </ProjectDescription>
                            )}

                            {share.projectLocation && (
                              <ShareInfoRow>
                                <ShareInfoLabel>Localização:</ShareInfoLabel>
                                <ShareInfoValue>{share.projectLocation}</ShareInfoValue>
                              </ShareInfoRow>
                            )}

                            {share.projectReturn && (
                              <ShareInfoRow>
                                <ShareInfoLabel>Retorno Esperado:</ShareInfoLabel>
                                <ShareInfoValue return>{share.projectReturn}%</ShareInfoValue>
                              </ShareInfoRow>
                            )}
                          </ShareCardBody>

                          <ShareCardFooter>
                            <ShareActions>
                              <ShareActionButton onClick={() => handleViewProjectDetails(share.projectId)}>
                                Ver Detalhes
                              </ShareActionButton>
                              <ShareActionButton onClick={() => handleAddDividend(share.currency)}>
                                + Dividendo
                              </ShareActionButton>
                            </ShareActions>
                          </ShareCardFooter>
                        </ShareCard>
                      ))}
                    </SharesGrid>

                    {/* Shares Summary */}
                    {filteredShares.length > 0 && (
                      <SharesSummary>
                        <SummaryItem>
                          <SummaryLabel>Total de Projetos:</SummaryLabel>
                          <SummaryValue>{new Set(filteredShares.map(s => s.projectId)).size}</SummaryValue>
                        </SummaryItem>
                        <SummaryItem>
                          <SummaryLabel>Total de Ações:</SummaryLabel>
                          <SummaryValue>{filteredShares.reduce((sum, s) => sum + (s.numberOfShares || 0), 0)}</SummaryValue>
                        </SummaryItem>
                        <SummaryItem>
                          <SummaryLabel>Valor Total em BRL:</SummaryLabel>
                          <SummaryValue highlight>R$ {calculateTotalSharesInBRL()}</SummaryValue>
                        </SummaryItem>
                      </SharesSummary>
                    )}
                  </>
                )}
              </>
            )}
          </DetailedSharesCard>

          <Spacing lg="60" md="30" />

          {/* Informações de Acesso */}
          <AccessInfoCard>
            <CardTitle>Informações de Acesso</CardTitle>
            <AccessGrid>
              <AccessItem>
                <AccessLabel>Nível de Acesso</AccessLabel>
                <AccessLevelBadge color={getAccessLevelColor(user.accessLevel)}>
                  {getAccessLevelText(user.accessLevel)}
                </AccessLevelBadge>
              </AccessItem>
              <AccessItem>
                <AccessLabel>Verificado</AccessLabel>
                <AccessValue>{user.verified ? "Sim" : "Não"}</AccessValue>
              </AccessItem>
              <AccessItem>
                <AccessLabel>Investidor</AccessLabel>
                <AccessValue>{user.investor ? "Sim" : "Não"}</AccessValue>
              </AccessItem>
              <AccessItem>
                <AccessLabel>Aprovado CVM</AccessLabel>
                <AccessValue>{user.cvmApproved ? "Sim" : "Não"}</AccessValue>
              </AccessItem>
              <AccessItem>
                <AccessLabel>Consultor ID</AccessLabel>
                <AccessValue>{user.consultantId || "N/A"}</AccessValue>
              </AccessItem>
              <AccessItem>
                <AccessLabel>Nome do Consultor</AccessLabel>
                <AccessValue>{user.consultantName || "N/A"}</AccessValue>
              </AccessItem>
            </AccessGrid>
          </AccessInfoCard>

          <Spacing lg="60" md="30" />

          {/* User Transactions Section */}
          <TransactionsSection>
            <TransactionsHeader>
              <CardTitle>Histórico de Transações do Usuário</CardTitle>
              <AddTransactionButton onClick={() => setIsAddTransactionModalOpen(true)}>
                + Adicionar Transação
              </AddTransactionButton>
            </TransactionsHeader>
            
                                    {!user ? (
              <TransactionsLoadingIndicator>
                <LoaderComponent size={30} borderThickness={4} />
                <span>Carregando dados do usuário...</span>
              </TransactionsLoadingIndicator>
            ) : (
              <>
                <TransactionsFilters>
                  <FilterSelect 
                    value={transactionsCurrencyFilter}
                    onChange={(e) => setTransactionsCurrencyFilter(e.target.value)}
                  >
                    <option value="ALL">Todas as Moedas</option>
                    <option value="BRL">BRL</option>
                    <option value="BTC">BTC</option>
                    <option value="USDT">USDT</option>
                  </FilterSelect>

                  <FilterSelect 
                    value={transactionsTypeFilter}
                    onChange={(e) => setTransactionsTypeFilter(e.target.value)}
                  >
                    <option value="ALL">Todos os Tipos</option>
                    <option value="deposit">Depósito</option>
                    <option value="withdraw">Saque</option>
                    <option value="spent">Gasto</option>
                    <option value="earning">Ganho</option>
                    <option value="commission">Comissão</option>
                  </FilterSelect>
                </TransactionsFilters>

                {transactionsLoading && (
                  <TransactionsLoadingIndicator>
                    <LoaderComponent size={30} borderThickness={4} />
                    <span>Carregando transações...</span>
                  </TransactionsLoadingIndicator>
                )}

                <TransactionsTableContainer>
                  <TransactionsTable>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Data</th>
                        <th>Hora</th>
                        <th>Moeda</th>
                        <th>Tipo</th>
                        <th>Status</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsLoading ? (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center", padding: "50px 0" }}>
                            <div style={{
                              width: "100%",
                              height: "200px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>
                              <LoaderComponent size={30} borderThickness={4} />
                            </div>
                          </td>
                        </tr>
                      ) : filteredTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{
                            textAlign: "center",
                            padding: "50px 0",
                            fontSize: "1.2rem",
                            color: "#666"
                          }}>
                            <div style={{
                              width: "100%",
                              height: "200px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}>
                              <h3>
                                {transactionsCurrencyFilter === 'ALL' && transactionsTypeFilter === 'ALL'
                                  ? "Nenhuma transação encontrada"
                                  : "Nenhuma transação encontrada com os filtros aplicados"}
                              </h3>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredTransactions.map((transaction, index) => (
                          <tr
                            key={transaction.id}
                            style={{
                              backgroundColor: index % 2 === 0 ? "transparent" : "#242424",
                            }}
                          >
                            <td>{transaction.id}</td>
                            <td>{formatTransactionDate(transaction.timestamp)}</td>
                            <td>{formatTransactionTime(transaction.timestamp)}</td>
                            <td>{transaction.currency || "BRL"}</td>
                            <td>
                              {transaction.type === "deposit" ? "Depósito" :
                               transaction.type === "withdraw" ? "Saque" :
                               transaction.type === "spent" ? "Gasto" :
                               transaction.type === "earning" ? "Ganho" :
                               transaction.type === "commission" ? "Comissão" :
                               transaction.type === "transfer" ? "Transferência" :
                               transaction.type === "fee" ? "Taxa" :
                               transaction.type}
                            </td>
                            <td>
                              <TransactionStatus status={transaction.status || 'completed'}>
                                {transaction.status === 'completed' ? 'Concluída' :
                                 transaction.status === 'pending' ? 'Pendente' :
                                 transaction.status === 'failed' ? 'Falhou' :
                                 transaction.status === 'cancelled' ? 'Cancelada' : 'Concluída'}
                              </TransactionStatus>
                            </td>
                            <td
                              style={{
                                color: transaction.type.toLowerCase() === "deposit" ? "#009A00" :
                                       transaction.type.toLowerCase() === "withdraw" ? "#E40000" :
                                       transaction.type.toLowerCase() === "spent" ? "#14FFFF" :
                                       transaction.type.toLowerCase() === "earning" ? "#00FF00" :
                                       transaction.type.toLowerCase() === "commission" ? "#00FF00" :
                                       "inherit",
                                fontWeight: "bold",
                              }}
                            >
                              {formatTransactionAmount(
                                transaction.amount,
                                transaction.type,
                                transaction.currency
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </TransactionsTable>
                </TransactionsTableContainer>

                {/* Paginação */}
                {transactionsTotalPages > 1 && (
                  <TransactionsPagination>
                    <PaginationButton
                      onClick={() => handleTransactionsPageChange(Math.max(transactionsCurrentPage - 1, 1))}
                      disabled={transactionsCurrentPage === 1}
                    >
                      Anterior
                    </PaginationButton>
                    <PaginationInfo>
                      Página {transactionsCurrentPage} de {transactionsTotalPages}
                    </PaginationInfo>
                    <PaginationButton
                      onClick={() => handleTransactionsPageChange(Math.min(transactionsCurrentPage + 1, transactionsTotalPages))}
                      disabled={transactionsCurrentPage === transactionsTotalPages}
                    >
                      Próxima
                    </PaginationButton>
                  </TransactionsPagination>
                )}
              </>
            )}
          </TransactionsSection>
        </div>

        {/* Modal de Dividendos */}
        {isDividendModalOpen && (
          <DividendModalOverlay>
            <DividendModal>
              <DividendModalHeader>
                <DividendModalTitle>Adicionar Dividendo</DividendModalTitle>
                <CloseButton onClick={handleCloseDividendModal}>×</CloseButton>
              </DividendModalHeader>
              <DividendModalBody>
                <DividendFormGroup>
                  <DividendLabel>Dividendo será aplicado a todos os projetos do usuário em {dividendCurrency}</DividendLabel>
                </DividendFormGroup>
                <DividendFormGroup>
                  <DividendLabel>Porcentagem do Dividendo:</DividendLabel>
                  <DividendInput
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={dividendAmount}
                    onChange={(e) => setDividendAmount(e.target.value)}
                    placeholder="Digite a porcentagem (ex: 5.5)"
                  />
                  <DividendHelpText>Digite um valor entre 0 e 100%</DividendHelpText>
                </DividendFormGroup>
                
                <DividendFormGroup>
                  <DividendLabel>Porcentagens Rápidas:</DividendLabel>
                  <QuickPercentageGrid>
                    <QuickPercentageButton onClick={() => handleQuickPercentage(1)}>
                      1%
                    </QuickPercentageButton>
                    <QuickPercentageButton onClick={() => handleQuickPercentage(1.5)}>
                      1.5%
                    </QuickPercentageButton>
                    <QuickPercentageButton onClick={() => handleQuickPercentage(2)}>
                      2%
                    </QuickPercentageButton>
                    <QuickPercentageButton onClick={() => handleQuickPercentage(0.99)}>
                      0.99%
                    </QuickPercentageButton>
                    <QuickPercentageButton onClick={() => handleQuickPercentage(1.49)}>
                      1.49%
                    </QuickPercentageButton>
                    <QuickPercentageButton onClick={() => handleQuickPercentage(1.99)}>
                      1.99%
                    </QuickPercentageButton>
                  </QuickPercentageGrid>
                </DividendFormGroup>
                <DividendButtonGroup>
                  <DividendCancelButton onClick={handleCloseDividendModal}>
                    Cancelar
                  </DividendCancelButton>
                  <DividendSubmitButton onClick={handleSubmitDividend}>
                    Adicionar Dividendo
                  </DividendSubmitButton>
                </DividendButtonGroup>
              </DividendModalBody>
            </DividendModal>
          </DividendModalOverlay>
        )}

        {/* Add Transaction Modal */}
        {isAddTransactionModalOpen && (
          <AddTransactionModalOverlay>
            <AddTransactionModal>
              <AddTransactionModalHeader>
                <AddTransactionModalTitle>Adicionar Transação</AddTransactionModalTitle>
                <CloseButton onClick={() => setIsAddTransactionModalOpen(false)}>×</CloseButton>
              </AddTransactionModalHeader>
              <AddTransactionModalBody>
                <TransactionFormGroup>
                  <TransactionLabel>Tipo de Transação:</TransactionLabel>
                  <TransactionSelect
                    value={newTransaction.type}
                    onChange={(e) => setNewTransaction({...newTransaction, type: e.target.value})}
                  >
                    <option value="deposit">Depósito</option>
                    <option value="withdraw">Saque</option>
                    <option value="spent">Gasto</option>
                    <option value="earning">Ganho</option>
                    <option value="commission">Comissão</option>
                    <option value="transfer">Transferência</option>
                    <option value="fee">Taxa</option>
                  </TransactionSelect>
                </TransactionFormGroup>

                <TransactionFormGroup>
                  <TransactionLabel>Valor:</TransactionLabel>
                  <TransactionInput
                    type="number"
                    step="0.01"
                    min="0"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})}
                    placeholder="Digite o valor"
                  />
                  <TransactionHelpText>
                    {newTransaction.currency === 'BTC' ? 'Valor em BTC (ex: 0.001)' : 
                     newTransaction.currency === 'USDT' ? 'Valor em USDT (ex: 100.50)' : 
                     'Valor em BRL (ex: 150.75)'}
                  </TransactionHelpText>
                </TransactionFormGroup>

                <TransactionFormGroup>
                  <TransactionLabel>Moeda:</TransactionLabel>
                  <TransactionSelect
                    value={newTransaction.currency}
                    onChange={(e) => setNewTransaction({...newTransaction, currency: e.target.value})}
                  >
                    <option value="BRL">BRL</option>
                    <option value="BTC">BTC</option>
                    <option value="USDT">USDT</option>
                  </TransactionSelect>
                </TransactionFormGroup>

                <TransactionFormGroup>
                  <TransactionLabel>Data e Hora:</TransactionLabel>
                  <TransactionInput
                    type="datetime-local"
                    value={newTransaction.timestamp}
                    onChange={(e) => setNewTransaction({...newTransaction, timestamp: e.target.value})}
                  />
                  <TransactionHelpText>
                    Selecione a data e hora da transação (padrão: data/hora atual)
                  </TransactionHelpText>
                </TransactionFormGroup>

                <TransactionFormGroup>
                  <TransactionLabel>Status:</TransactionLabel>
                  <TransactionSelect
                    value={newTransaction.status}
                    onChange={(e) => setNewTransaction({...newTransaction, status: e.target.value})}
                  >
                    <option value="completed">Concluída</option>
                    <option value="pending">Pendente</option>
                    <option value="failed">Falhou</option>
                    <option value="cancelled">Cancelada</option>
                  </TransactionSelect>
                </TransactionFormGroup>

                <TransactionFormGroup>
                  <TransactionLabel>Descrição (Opcional):</TransactionLabel>
                  <TransactionTextarea
                    value={newTransaction.description}
                    onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
                    placeholder="Descrição da transação"
                    rows="3"
                  />
                </TransactionFormGroup>

                <TransactionButtonGroup>
                  <TransactionCancelButton onClick={() => setIsAddTransactionModalOpen(false)}>
                    Cancelar
                  </TransactionCancelButton>
                  <TransactionSubmitButton onClick={handleAddTransaction}>
                    Adicionar Transação
                  </TransactionSubmitButton>
                </TransactionButtonGroup>
              </AddTransactionModalBody>
            </AddTransactionModal>
          </AddTransactionModalOverlay>
        )}

        <Spacing lg="145" md="80" />
      </>
    );
  };

  // Styled Components
  const BackButton = styled.button`
    background: none;
    border: none;
    color: #c09c5c;
    font-size: 16px;
    cursor: pointer;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: color 0.2s ease;

    &:hover {
      color: #a07c4b;
    }
  `;

  const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 100px;
  `;

  const ErrorContainer = styled.div`
    text-align: center;
    padding: 100px;
    color: #fff;

    h2 {
      margin-bottom: 20px;
    }
  `;

  const UserInfoCard = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const UserHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 30px;
  `;

  const UserAvatar = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
  `;

  const UserInfo = styled.div`
    flex: 1;
  `;

  const UserName = styled.h3`
    color: #fff;
    margin: 0 0 8px 0;
    font-size: 24px;
  `;

  const UserEmail = styled.p`
    color: #c09c5c;
    margin: 0 0 4px 0;
    font-size: 16px;
  `;

  const UserId = styled.p`
    color: #888;
    margin: 0;
    font-size: 14px;
  `;

  const StatusBadge = styled.span`
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    background-color: ${props => props.color}20;
    color: ${props => props.color};
    border: 1px solid ${props => props.color};
  `;

  const UserDetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  `;

  const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;

  const DetailLabel = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
  `;

  const DetailValue = styled.span`
    color: #fff;
    font-size: 16px;
    font-weight: 600;
  `;

  const FinancialInfoCard = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const CardTitle = styled.h3`
    color: #c09c5c;
    margin: 0 0 20px 0;
    font-size: 20px;
    font-weight: 600;
  `;

  const FinancialCardsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
    
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `;

  const FinancialCard = styled.div`
    background-color: #151515;
    border-radius: 10px;
    padding: 20px;
    border: 1px solid #333;
    height: 100%;
    display: flex;
    flex-direction: column;
  `;

  const FinancialCardHeader = styled.h4`
    color: #c09c5c;
    margin: 0 0 15px 0;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
    padding: 10px;
    background-color: #1a1a1a;
    border-radius: 6px;
    border: 1px solid #333;
  `;

  const FinancialCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    justify-content: space-between;
  `;

  const FinancialItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #fff;
    font-size: 16px;
    padding: 8px 0;
    flex-wrap: wrap;
    gap: 8px;
  `;

  const FinancialLabel = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
    flex: 1;
  `;

  const FinancialValue = styled.span`
    color: ${props => {
      if (props.highlight) return "#10b981";
      if (props.profit !== undefined) {
        return props.profit ? "#10b981" : "#ef4444";
      }
      return "#fff";
    }};
    font-size: 16px;
    font-weight: 600;
    font-family: 'Roboto Mono', monospace;
    text-align: right;
    min-width: 120px;
    margin-left: 10px;
  `;

  const FinancialDivider = styled.div`
    height: 1px;
    background-color: #333;
    margin: 10px 0;
    width: 100%;
  `;

  const DistributionSection = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const DistributionTitle = styled.h4`
    color: #c09c5c;
    margin: 0 0 15px 0;
    font-size: 18px;
    font-weight: 600;
  `;

  const DistributionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
  `;

  const DistributionItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  const DistributionLabel = styled.span`
    color: #888;
    font-size: 14px;
    margin-bottom: 5px;
  `;

  const DistributionBar = styled.div`
    width: 100%;
    height: 10px;
    background-color: #333;
    border-radius: 5px;
    overflow: hidden;
    margin-bottom: 5px;
  `;

  const DistributionFill = styled.div`
    height: 100%;
    background-color: ${props => props.color};
    border-radius: 5px;
    width: ${props => props.width};
  `;

  const DistributionValue = styled.span`
    color: #fff;
    font-size: 14px;
    font-weight: 600;
  `;

  const AccessInfoCard = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const AccessGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  `;

  const AccessItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;

  const AccessLabel = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
  `;

  const AccessValue = styled.span`
    color: #fff;
    font-size: 16px;
    font-weight: 600;
  `;

  const AccessLevelBadge = styled.span`
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    background-color: ${props => props.color}20;
    color: ${props => props.color};
    border: 1px solid ${props => props.color};
    display: inline-block;
    width: fit-content;
  `;

  const SystemInfoCard = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const SystemGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  `;

  const SystemItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;

  const SystemLabel = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
  `;

  const SystemValue = styled.span`
    color: #fff;
    font-size: 16px;
    font-weight: 600;
  `;

  const TabContainer = styled.div`
    display: flex;
    gap: 10px;
    border-bottom: 1px solid #333;
  `;

  const TabButton = styled.button`
    padding: 12px 24px;
    background: none;
    border: none;
    color: ${props => props.active ? "#c09c5c" : "#888"};
    font-size: 16px;
    cursor: pointer;
    border-bottom: 2px solid ${props => props.active ? "#c09c5c" : "transparent"};
    transition: all 0.2s ease;

    &:hover {
      color: #c09c5c;
    }
  `;

  const OverviewTab = styled.div``;

  const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
  `;

  const StatCard = styled.div`
    background-color: #1a1a1a;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    border: 1px solid #333;
  `;

  const StatValue = styled.div`
    color: #c09c5c;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 8px;
  `;

  const StatLabel = styled.div`
    color: #888;
    font-size: 14px;
  `;

  const TransactionsTab = styled.div``;

  const EmptyState = styled.div`
    text-align: center;
    padding: 60px;
    color: #888;
    font-size: 16px;
    background-color: #1a1a1a;
    border-radius: 8px;
    border: 1px solid #333;
  `;

  const InvestmentsTab = styled.div``;

  // Styled Components para Dividendos
  const DividendButton = styled.button`
    background-color: #10b981;
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
    margin-left: 8px;

    &:hover {
      background-color: #059669;
    }
  `;

  const DividendModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  `;

  const DividendModal = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 0;
    width: 90%;
    max-width: 500px;
    border: 1px solid #333;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  `;

  const DividendModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    border-bottom: 1px solid #333;
  `;

  const DividendModalTitle = styled.h3`
    color: #c09c5c;
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  `;

  const CloseButton = styled.button`
    background: none;
    border: none;
    color: #888;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover {
      background-color: #333;
      color: #fff;
    }
  `;

  const DividendModalBody = styled.div`
    padding: 30px;
  `;

  const DividendFormGroup = styled.div`
    margin-bottom: 20px;
  `;

  const DividendLabel = styled.label`
    display: block;
    color: #888;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  `;

  const DividendInput = styled.input`
    width: 100%;
    padding: 12px;
    border: 1px solid #333;
    border-radius: 6px;
    background-color: #151515;
    color: #fff;
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #c09c5c;
    }

    &::placeholder {
      color: #666;
    }
  `;



  const DividendHelpText = styled.p`
    color: #666;
    font-size: 12px;
    margin: 4px 0 0 0;
    font-style: italic;
  `;

  const QuickPercentageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 8px;
  `;

  const QuickPercentageButton = styled.button`
    background-color: #333;
    color: #fff;
    border: 1px solid #555;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #c09c5c;
      border-color: #c09c5c;
      color: #fff;
    }

    &:active {
      transform: scale(0.95);
    }
  `;

  const DividendButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 30px;
  `;

  const DividendCancelButton = styled.button`
    background-color: #6b7280;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #4b5563;
    }
  `;

  const DividendSubmitButton = styled.button`
    background-color: #10b981;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #059669;
    }
  `;

  // Transactions Section Styled Components
  const TransactionsSection = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const TransactionsFilters = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  `;

  const FilterSelect = styled.select`
    padding: 8px 15px;
    border-radius: 5px;
    background-color: #242424;
    color: #fff;
    border: 2px solid #BF9A5D;
    cursor: pointer;
    font-size: 14px;
    min-width: 150px;

    &:focus {
      outline: none;
      border-color: #c09c5c;
    }
  `;

  const TransactionsTableContainer = styled.div`
    overflow-x: auto;
    margin-bottom: 20px;
  `;

  const TransactionsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    color: #fff;
    font-size: 14px;

    th, td {
      padding: 12px 8px;
      text-align: left;
      border-bottom: 1px solid #333;
    }

    th {
      background-color: #242424;
      color: #c09c5c;
      font-weight: 600;
      font-size: 14px;
    }

    tbody tr:hover {
      background-color: #242424;
    }
  `;

  const TransactionsPagination = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin-top: 20px;
  `;

  const PaginationButton = styled.button`
    background-color: #c09c5c;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background-color: #a07c4b;
    }

    &:disabled {
      background-color: #666;
      cursor: not-allowed;
      opacity: 0.6;
    }
  `;

  const PaginationInfo = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
  `;

  const TransactionsLoadingIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 15px;
    color: #c09c5c;
    font-size: 14px;
    font-weight: 500;
  `;

  const TransactionsAccessDenied = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: #888;
    font-size: 16px;
    background-color: #151515;
    border-radius: 8px;
    border: 1px solid #333;
    margin: 20px 0;
  `;

  // Add Transaction Modal Styled Components
  const TransactionsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  `;

  const AddTransactionButton = styled.button`
    background-color: #10b981;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #059669;
    }
  `;

  const AddTransactionModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  `;

  const AddTransactionModal = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 0;
    width: 90%;
    max-width: 500px;
    border: 1px solid #333;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  `;

  const AddTransactionModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    border-bottom: 1px solid #333;
  `;

  const AddTransactionModalTitle = styled.h3`
    color: #c09c5c;
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  `;

  const AddTransactionModalBody = styled.div`
    padding: 30px;
  `;

  const TransactionFormGroup = styled.div`
    margin-bottom: 20px;
  `;

  const TransactionLabel = styled.label`
    display: block;
    color: #888;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  `;

  const TransactionHelpText = styled.p`
    color: #666;
    font-size: 12px;
    margin: 4px 0 0 0;
    font-style: italic;
  `;

  const TransactionInput = styled.input`
    width: 100%;
    padding: 12px;
    border: 1px solid #333;
    border-radius: 6px;
    background-color: #151515;
    color: #fff;
    font-size: 16px;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #c09c5c;
    }

    &::placeholder {
      color: #666;
    }
  `;

  const TransactionSelect = styled.select`
    width: 100%;
    padding: 12px;
    border: 1px solid #333;
    border-radius: 6px;
    background-color: #151515;
    color: #fff;
    font-size: 16px;
    transition: border-color 0.2s ease;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #c09c5c;
    }
  `;

  const TransactionTextarea = styled.textarea`
    width: 100%;
    padding: 12px;
    border: 1px solid #333;
    border-radius: 6px;
    background-color: #151515;
    color: #fff;
    font-size: 16px;
    transition: border-color 0.2s ease;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #c09c5c;
    }

    &::placeholder {
      color: #666;
    }
  `;

  const TransactionButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 30px;
  `;

  const TransactionCancelButton = styled.button`
    background-color: #6b7280;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #4b5563;
    }
  `;

  const TransactionSubmitButton = styled.button`
    background-color: #10b981;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #059669;
    }
  `;

  const TransactionStatus = styled.span`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    background-color: ${props => {
      switch (props.status) {
        case 'completed': return '#10b98120';
        case 'pending': return '#f59e0b20';
        case 'failed': return '#ef444420';
        case 'cancelled': return '#6b728020';
        default: return '#10b98120';
      }
    }};
    color: ${props => {
      switch (props.status) {
        case 'completed': return '#10b981';
        case 'pending': return '#f59e0b';
        case 'failed': return '#ef4444';
        case 'cancelled': return '#6b7280';
        default: return '#10b981';
      }
    }};
    border: 1px solid ${props => {
      switch (props.status) {
        case 'completed': return '#10b981';
        case 'pending': return '#f59e0b';
        case 'failed': return '#ef4444';
        case 'cancelled': return '#6b7280';
        default: return '#10b981';
      }
    }};
  `;

  // Shares Section Styled Components
  const DetailedSharesCard = styled.div`
    background-color: #1a1a1a;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #333;
  `;

  const SharesFilters = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  `;

  const SharesLoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 60px 20px;
    color: #c09c5c;
    font-size: 16px;
    font-weight: 500;
  `;

  const SharesEmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #888;
    font-size: 16px;
    background-color: #151515;
    border-radius: 8px;
    border: 1px solid #333;
  `;

  const SharesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
  `;

  const ShareCard = styled.div`
    background-color: #151515;
    border-radius: 12px;
    border: 1px solid #333;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
      border-color: #c09c5c;
    }
  `;

  const ShareCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 20px;
    background-color: #1a1a1a;
    border-bottom: 1px solid #333;
  `;

  const ProjectImage = styled.img`
    width: 60px;
    height: 60px;
    border-radius: 8px;
    object-fit: cover;
    border: 2px solid #333;
  `;

  const ProjectHeaderInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  `;

  const ProjectName = styled.span`
    color: #fff;
    font-weight: 600;
    font-size: 16px;
  `;

  const ProjectId = styled.span`
    color: #888;
    font-size: 12px;
  `;

  const ProjectCategory = styled.span`
    color: #c09c5c;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
  `;

  const CurrencyBadge = styled.span`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    background-color: ${props => {
      switch (props.currency) {
        case 'BTC': return '#f7931a20';
        case 'USDT': return '#26a17b20';
        default: return '#10b98120';
      }
    }};
    color: ${props => {
      switch (props.currency) {
        case 'BTC': return '#f7931a';
        case 'USDT': return '#26a17b';
        default: return '#10b981';
      }
    }};
    border: 1px solid ${props => {
      switch (props.currency) {
        case 'BTC': return '#f7931a';
        case 'USDT': return '#26a17b';
        default: return '#10b981';
      }
    }};
  `;

  const ShareCardBody = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `;

  const ShareInfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #333;
    
    &:last-child {
      border-bottom: none;
    }
  `;

  const ShareInfoLabel = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
    flex: 1;
  `;

  const ShareInfoValue = styled.span`
    color: ${props => {
      if (props.highlight) return '#c09c5c';
      if (props.return) return '#10b981';
      return '#fff';
    }};
    font-size: 14px;
    font-weight: 600;
    font-family: 'Roboto Mono', monospace;
    text-align: right;
    min-width: 120px;
    margin-left: 10px;
  `;

  const ProjectDescription = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 0;
    border-top: 1px solid #333;
    border-bottom: 1px solid #333;
  `;

  const ProjectDescriptionText = styled.span`
    color: #ccc;
    font-size: 13px;
    line-height: 1.4;
    font-style: italic;
  `;

  const ShareCardFooter = styled.div`
    padding: 20px;
    background-color: #1a1a1a;
    border-top: 1px solid #333;
  `;

  const ShareActions = styled.div`
    display: flex;
    gap: 10px;
    justify-content: space-between;
  `;

  const ShareActionButton = styled.button`
    flex: 1;
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:first-child {
      background-color: #c09c5c;
      color: #fff;

      &:hover {
        background-color: #a07c4b;
      }
    }

    &:last-child {
      background-color: #10b981;
      color: #fff;

      &:hover {
        background-color: #059669;
      }
    }
  `;

  const ShareStatus = styled.span`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    background-color: ${props => {
      switch (props.status) {
        case 'active': return '#10b98120';
        case 'pending': return '#f59e0b20';
        case 'sold': return '#6b728020';
        case 'cancelled': return '#ef444420';
        default: return '#10b98120';
      }
    }};
    color: ${props => {
      switch (props.status) {
        case 'active': return '#10b981';
        case 'pending': return '#f59e0b';
        case 'sold': return '#6b7280';
        case 'cancelled': return '#ef4444';
        default: return '#10b981';
      }
    }};
    border: 1px solid ${props => {
      switch (props.status) {
        case 'active': return '#10b981';
        case 'pending': return '#f59e0b';
        case 'sold': return '#6b7280';
        case 'cancelled': return '#ef4444';
        default: return '#10b981';
      }
    }};
  `;

  const SharesLoadingIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 15px;
    color: #c09c5c;
    font-size: 14px;
    font-weight: 500;
  `;

  const SharesSummary = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-top: 20px;
    padding: 20px;
    background-color: #151515;
    border-radius: 8px;
    border: 1px solid #333;
  `;



  const SummaryItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  `;

  const SummaryLabel = styled.span`
    color: #888;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  `;

  const SummaryValue = styled.span`
    color: ${props => props.highlight ? '#c09c5c' : '#fff'};
    font-size: 18px;
    font-weight: 700;
    font-family: 'Roboto Mono', monospace;
  `;

  export default ClientDetails;